import React from 'react';
import { CmsCommonControls } from 'js/model/cms/cms-common-controls';
import { VenueListingSpecificationOutput } from 'js/model/rainbow/browse-page/VenueListingSpecificationOutput';
import {
  DateTimeInput,
  DateTimeInputHandle,
} from 'js/components/controls/DateTimeInput';
import { CmsCalendar } from 'js/model/cms/cms-calendar';
import { dayHourList } from 'js/helpers/time';
import { parse, format } from 'date-fns';
import {
  ChangeBrowseDataFunction,
  changeBrowseDataContext,
  browseRemovalParameterType,
  AddLocation,
} from 'js/pages/BrowsePage/BrowseData';
import { VenueListingCriteria } from 'js/model/rainbow/browse-page/VenueListingCriteria';
import { ClearableMode } from 'js/components/controls/DropdownInput/DropdownInput';
import { TrackingDetails } from 'js/components/controls/DateTimeInput/TrackingDetails';
import { FORMAT } from 'js/helpers/datetime-format';
import styles from './WhatWhereWhen.module.css';
import { trackDatetime, Category } from './tracking';

interface Props {
  cmsCalendar: CmsCalendar;
  cmsCommonControls: CmsCommonControls;
  specification: VenueListingSpecificationOutput;
  trackingCategory: Category;
  visible?: boolean;
  onFocus?: () => void;
}

interface State {
  date: string | null;
  startHours: number | null;
  endHours: number | null;
}

export class DateTimeSearchInput extends React.PureComponent<Props, State> {
  private dateTimeInputRef: React.RefObject<DateTimeInputHandle | null> =
    React.createRef();

  public static contextType = changeBrowseDataContext;

  public state: State = {
    date: this.props.specification.date
      ? this.props.specification.date.from
      : null,
    startHours:
      this.props.specification.startHours !== undefined
        ? this.props.specification.startHours
        : null,
    endHours:
      this.props.specification.endHours !== undefined
        ? this.props.specification.endHours
        : null,
  };

  public componentDidMount(): void {
    this.setDateTime();
  }

  public componentDidUpdate(prevProps: Props): void {
    const { startHours, endHours } = this.props.specification;
    const date =
      this.props.specification.date && this.props.specification.date.from;

    const prevStartHours = prevProps.specification.startHours;
    const prevEndHours = prevProps.specification.endHours;
    const prevDate =
      prevProps.specification.date && prevProps.specification.date.from;

    if (
      startHours === prevStartHours &&
      endHours === prevEndHours &&
      date === prevDate
    ) {
      return;
    }

    this.setState(
      {
        date: this.props.specification.date
          ? this.props.specification.date.from
          : null,
        startHours:
          this.props.specification.startHours !== undefined
            ? this.props.specification.startHours
            : null,
        endHours:
          this.props.specification.endHours !== undefined
            ? this.props.specification.endHours
            : null,
      },
      () => {
        this.setDateTime();
      }
    );
  }

  private setDateTime = (): void => {
    if (this.dateTimeInputRef.current) {
      const specifiedDate = this.state.date
        ? parse(this.state.date, FORMAT, new Date())
        : null;

      const startHour = this.state.startHours
        ? dayHourList(false, this.state.startHours)
        : null;
      const endHour = this.state.endHours
        ? dayHourList(false, this.state.endHours)
        : null;

      // TODO: In future should give a props -date, timeFrom, timeTo to DateTimeInput component to avoid using ref object
      this.dateTimeInputRef.current.setDateTime(
        specifiedDate,
        startHour !== null ? startHour[0].value : null,
        endHour !== null ? endHour[0].value : null
      );
    }
  };

  private onDateTimeChange = (
    date: Date | null,
    timeFrom: string | null,
    timeTo: string | null
  ): void => {
    this.setState({
      date:
        date !== null
          ? format(date, FORMAT, {
              useAdditionalDayOfYearTokens: true,
              useAdditionalWeekYearTokens: true,
            })
          : null,
      startHours: timeFrom !== null ? Number(timeFrom.substr(0, 2)) : null,
      endHours: timeTo !== null ? Number(timeTo.substr(0, 2)) : null,
    });
  };

  public onBlurDateTimeInput = (): void => {
    if (this.hasChanged()) {
      const changeBrowseData = this.context as ChangeBrowseDataFunction;

      const criteria: VenueListingCriteria = {};
      const removeParameters = [];

      if (this.state.date !== null) {
        criteria.date = {
          from: format(parse(this.state.date, FORMAT, new Date()), FORMAT, {
            useAdditionalDayOfYearTokens: true,
            useAdditionalWeekYearTokens: true,
          }),
          to: format(parse(this.state.date, FORMAT, new Date()), FORMAT, {
            useAdditionalDayOfYearTokens: true,
            useAdditionalWeekYearTokens: true,
          }),
        };
      } else {
        removeParameters.push(browseRemovalParameterType.DATE);
      }

      if (this.state.startHours !== null) {
        criteria.startHours = Number(this.state.startHours);
      } else {
        removeParameters.push(browseRemovalParameterType.STARTHOURS);
      }

      if (this.state.endHours !== null) {
        criteria.endHours = Number(this.state.endHours);
      } else {
        removeParameters.push(browseRemovalParameterType.ENDHOURS);
      }

      changeBrowseData(
        criteria,
        removeParameters,
        AddLocation.CurrentPageWithRadius
      );
    }
  };

  private hasChanged = (): boolean => {
    const dateFrom = this.props.specification.date
      ? this.props.specification.date.from
      : null;
    const startHours =
      this.props.specification.startHours !== undefined
        ? this.props.specification.startHours
        : null;
    const endHours =
      this.props.specification.endHours !== undefined
        ? this.props.specification.endHours
        : null;

    if (
      this.state.date !== dateFrom ||
      this.state.startHours !== startHours ||
      this.state.endHours !== endHours
    ) {
      return true;
    }
    return false;
  };

  private onTrack = (details: TrackingDetails): void => {
    trackDatetime(this.props.trackingCategory, details);
  };

  public render(): React.ReactNode {
    return (
      <div
        className={this.props.visible ? styles.renderInput : styles.hideInput}
      >
        <DateTimeInput
          ref={this.dateTimeInputRef}
          cmsCalendar={this.props.cmsCalendar}
          cmsCommonControls={this.props.cmsCommonControls}
          selectableToEndOfMonth
          maxDaysInFuture={90}
          placeholder={
            this.props.cmsCommonControls['datetime-input'][
              'date-time-placeholder'
            ]
          }
          isClearable={ClearableMode.ClearableWhenActive}
          onChange={this.onDateTimeChange}
          onTrack={this.onTrack}
          onBlur={this.onBlurDateTimeInput}
          onFocus={this.props.onFocus}
          isPatternedBackground
        />
      </div>
    );
  }
}
